import React, { useState, useRef, useEffect, useCallback, ReactNode, ChangeEvent, KeyboardEvent, DragEvent } from "react";
import Icon from "@mdi/react";
import { mdiPaperclip, mdiArrowUp, mdiLightbulb, mdiClose, mdiCloudUploadOutline, mdiMicrophone } from "@mdi/js";
import { Box, Paper, IconButton, InputBase, CircularProgress, Popper, Chip, Typography } from "@mui/material";
import { NotistackServices } from "shared/services";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { MessageProps, Message } from "./Message";
import { ConfirmationMessage, ConfirmationMessageProps } from "./ConfirmationMessage";
import { OAuthActionMessage, OAuthActionMessageProps } from "./OAuthActionMessage";
import { useSizeEffect } from "shared/hooks";
import { parseCommand, isValidCommand } from "shared/utils/commandParser";

// Tipagem das Props do Chat
interface ChatProps {
	onSend: (message: string, files?: File[]) => Promise<void> | void;
	fileTypes?: string[];
	children?: ReactNode;
	className?: string;
	placeholder?: string;
	disabled?: boolean;
	enableCommandSuggestions?: boolean;
	/** Quando fornecido, exibe o botão de microfone (modo de voz). EK-442. */
	onVoiceCall?: () => void;
}

const Chat: React.FC<ChatProps> & {
	Message: React.FC<MessageProps>;
	Confirmation: React.FC<ConfirmationMessageProps>;
	OAuthAction: React.FC<OAuthActionMessageProps>;
} = ({
	onSend,
	fileTypes = [],
	children,
	className,
	placeholder = "Envie uma mensagem...",
	disabled = false,
	enableCommandSuggestions = true,
	onVoiceCall,
}) => {
	const [loading, setLoading] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const [showSuggestion, setShowSuggestion] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	// EK-408 polish: drag-drop. Counter (não boolean) lida com nested
	// dragenter/dragleave que disparam ao passar por filhos da área.
	const [isDragging, setIsDragging] = useState(false);
	const dragDepthRef = useRef(0);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const lastIdMessageRef = useRef<string | null>(null);
	const inputSpacerRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const inputContainerRef = useRef<HTMLDivElement>(null);

	// Auto-scroll inteligente: acompanha novas mensagens, mas pausa
	// se o usuário fizer scroll manual para cima.
	const isUserScrolledUpRef = useRef(false);

	const handleWrapperScroll = useCallback(() => {
		const el = wrapperRef.current;
		if (!el) return;
		// column-reverse: scrollTop === 0 = fundo; scrollTop < 0 = usuário subiu
		// Em browsers que usam valor positivo, comparamos com a distância restante
		const threshold = 80;
		const atBottom = Math.abs(el.scrollTop) <= threshold;
		isUserScrolledUpRef.current = !atBottom;
	}, []);

	const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
		messagesEndRef.current?.scrollIntoView({ behavior });
		isUserScrolledUpRef.current = false;
	}, []);

	// Scroll ao surgir nova mensagem (novo ID), exceto se usuário subiu
	useEffect(() => {
		const ids = React.Children.map(children, (child) => {
			if (React.isValidElement(child) && "props" in child && (child.props as any).id) {
				return (child.props as any).id;
			}
			return null;
		});

		const lastId = ids ? ids[ids.length - 1] : null;

		if (lastId !== lastIdMessageRef.current) {
			if (!isUserScrolledUpRef.current) {
				scrollToBottom();
			}
		}

		lastIdMessageRef.current = lastId;
	}, [children, scrollToBottom]);

	// Observer: auto-scroll durante streaming enquanto usuário está no fundo
	useEffect(() => {
		const sentinel = messagesEndRef.current;
		const container = wrapperRef.current;
		if (!sentinel || !container) return;

		const observer = new MutationObserver(() => {
			if (!isUserScrolledUpRef.current) {
				sentinel.scrollIntoView({ behavior: "auto" });
			}
		});

		observer.observe(container, { childList: true, subtree: true, characterData: true });
		return () => observer.disconnect();
	}, []);

	const handleSend = async () => {
		if (!inputValue.trim() && selectedFiles.length === 0) return;
		setLoading(true);
		scrollToBottom();
		const filesToSend = selectedFiles.length > 0 ? [...selectedFiles] : undefined;
		setSelectedFiles([]);
		if (fileInputRef.current) fileInputRef.current.value = "";
		try {
			await Promise.all([onSend(inputValue, filesToSend), new Promise((resolve) => setTimeout(resolve, 1000))]);
			setInputValue("");
		} finally {
			setLoading(false);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleFileClick = () => {
		fileInputRef.current?.click();
	};

	const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100 MB — bate com o cap do backend (uploadChatAttachment.ts) e o client_max_body_size do nginx
	const MAX_FILE_MB = MAX_FILE_BYTES / 1024 / 1024;

	const addIncomingFiles = useCallback((incoming: File[]) => {
		const valid = incoming.filter((f) => f.size <= MAX_FILE_BYTES);
		const rejected = incoming.filter((f) => f.size > MAX_FILE_BYTES);
		if (rejected.length) {
			NotistackServices.showErrorMessage(
				`${rejected.map((f) => f.name).join(", ")} excede o limite de ${MAX_FILE_MB} MB e não foi adicionado.`,
			);
		}
		if (valid.length) setSelectedFiles((prev) => [...prev, ...valid]);
	}, [MAX_FILE_BYTES, MAX_FILE_MB]);

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		addIncomingFiles(Array.from(e.target.files ?? []));
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleRemoveFile = (idx: number) => {
		setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
	};

	// EK-408 polish: drag-drop handlers no container do chat.
	const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
		if (loading || disabled) return;
		// Só ativa se o cursor estiver carregando arquivo. Evita destacar
		// quando user arrasta texto/elemento da própria página.
		if (!e.dataTransfer?.types?.includes("Files")) return;
		e.preventDefault();
		dragDepthRef.current += 1;
		setIsDragging(true);
	}, [loading, disabled]);

	const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
		if (loading || disabled) return;
		if (!e.dataTransfer?.types?.includes("Files")) return;
		e.preventDefault();
		// "copy" mostra o ícone certo no cursor.
		e.dataTransfer.dropEffect = "copy";
	}, [loading, disabled]);

	const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
		if (!e.dataTransfer?.types?.includes("Files")) return;
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
		if (dragDepthRef.current === 0) setIsDragging(false);
	}, []);

	const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
		if (loading || disabled) return;
		if (!e.dataTransfer?.types?.includes("Files")) return;
		e.preventDefault();
		dragDepthRef.current = 0;
		setIsDragging(false);
		const files = Array.from(e.dataTransfer.files ?? []);
		if (files.length) addIncomingFiles(files);
	}, [loading, disabled, addIncomingFiles]);

	// Parser de comandos em linguagem natural
	const parsedCommand = React.useMemo(() => {
		if (!enableCommandSuggestions || !inputValue.trim()) return null;
		return parseCommand(inputValue);
	}, [inputValue, enableCommandSuggestions]);

	const shouldShowSuggestion = React.useMemo(() => {
		if (!parsedCommand) return false;
		return isValidCommand(parsedCommand) && parsedCommand.intent !== "none";
	}, [parsedCommand]);

	const handleAcceptSuggestion = useCallback(() => {
		if (parsedCommand && isValidCommand(parsedCommand)) {
			// Usar a descrição completa com a sugestão
			const suggestion = parsedCommand.suggestion || "";
			const description = parsedCommand.parameters.description || inputValue;
			const fullText = `${suggestion}\n\n${description}`.trim();
			setInputValue(fullText);
			setShowSuggestion(false);
		}
	}, [parsedCommand]);

	const inputAreaRef = useSizeEffect<HTMLDivElement>((size) => {
		if (inputSpacerRef.current) {
			inputSpacerRef.current.style.height = `${size.height}px`;
		}
	});

	return (
		<Box
			className={clsx(styles.chatContainer, className)}
			onDragEnter={handleDragEnter}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{isDragging && (
				<div className={styles.dragOverlay}>
					<Icon path={mdiCloudUploadOutline} size={2.5} />
					<span>Solte os arquivos para anexar</span>
				</div>
			)}
			<div
				className={styles.messagesWrapper}
				ref={wrapperRef}
				onScroll={handleWrapperScroll}
			>
				<Box className={styles.messagesList}>
					{children}
					<div
						className={styles.inputSpacer}
						ref={inputSpacerRef}
					/>
					<div ref={messagesEndRef} />
				</Box>
			</div>

			<Box
				className={styles.inputArea}
				ref={inputAreaRef}
			>
				<Paper
					className={styles.inputContainer}
					elevation={0}
					ref={inputContainerRef}
				>
					<IconButton
						className={styles.actionButton}
						onClick={handleFileClick}
						title="Anexar arquivo"
						size="small"
						disabled={loading || disabled}
					>
						<Icon
							path={mdiPaperclip}
							size={1}
						/>
					</IconButton>

					{onVoiceCall && (
						<IconButton
							className={styles.actionButton}
							onClick={onVoiceCall}
							title="Conversar por voz"
							aria-label="Conversar por voz"
							size="small"
							disabled={disabled}
						>
							<Icon
								path={mdiMicrophone}
								size={1}
							/>
						</IconButton>
					)}

					<input
						type="file"
						ref={fileInputRef}
						className={styles.fileInput}
						accept={fileTypes.join(",")}
						onChange={handleFileChange}
						multiple
					/>

					<div style={{ minHeight: "34px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
						{selectedFiles.length > 0 && (
							<Box className={styles.filePreviewRow}>
								{selectedFiles.map((file, idx) => (
									<Chip
										key={idx}
										label={file.name}
										size="small"
										onDelete={() => handleRemoveFile(idx)}
										deleteIcon={<Icon path={mdiClose} size={0.55} />}
										className={styles.fileChip}
									/>
								))}
							</Box>
						)}
						<InputBase
							name="messageInput"
							className={styles.textArea}
							placeholder={placeholder}
							multiline
							maxRows={6}
							value={inputValue}
							onChange={(e) => {
								setInputValue(e.target.value);
								setShowSuggestion(true);
							}}
							onKeyDown={handleKeyDown}
							disabled={loading || disabled}
						/>
					</div>

					<IconButton
						className={clsx(styles.actionButton, styles.sendButton)}
						onClick={handleSend}
						disabled={(inputValue.trim() === "" && selectedFiles.length === 0) || loading || disabled}
						title="Enviar"
						size="small"
					>
						{loading ? (
							<CircularProgress size={20} />
						) : (
							<Icon
								path={mdiArrowUp}
								size={1}
							/>
						)}
					</IconButton>
				</Paper>

				{/* Sugestão de comando em linguagem natural */}
				{shouldShowSuggestion && parsedCommand && (
					<Popper
						open={showSuggestion}
						anchorEl={inputContainerRef.current}
						placement="top-start"
						style={{ zIndex: 1300 }}
					>
						<Paper
							onClick={handleAcceptSuggestion}
							sx={{
								p: 1.5,
								mb: 1,
								background: "rgba(var(--mui-palette-primary-mainChannel) / 0.08)",
								border: "1px solid",
								borderColor: "primary.light",
								borderRadius: 1,
								maxWidth: 350,
								cursor: "pointer",
								transition: "all 0.2s ease",
								"&:hover": {
									background: "rgba(var(--mui-palette-primary-mainChannel) / 0.12)",
									borderColor: "primary.main",
								},
							}}
						>
							<Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
								<Icon path={mdiLightbulb} size={0.8} color="primary" style={{ marginTop: 2 }} />
								<Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }}>
									<strong>Sugestão:</strong> {parsedCommand.suggestion}
								</Typography>
							</Box>

							{parsedCommand.parameters && (
								<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
									{parsedCommand.parameters.title && (
										<Chip
											size="small"
											label={`📝 ${parsedCommand.parameters.title}`}
											variant="outlined"
											sx={{ height: 24 }}
										/>
									)}
									{parsedCommand.parameters.priority && (
										<Chip
											size="small"
											label={`🎯 ${parsedCommand.parameters.priority}`}
											color="warning"
											variant="outlined"
											sx={{ height: 24 }}
										/>
									)}
									{parsedCommand.parameters.deadline && (
										<Chip
											size="small"
											label={`📅 ${parsedCommand.parameters.deadline}`}
											color="info"
											variant="outlined"
											sx={{ height: 24 }}
										/>
									)}
								</Box>
							)}

							<Typography
								variant="caption"
								sx={{ display: "block", color: "text.secondary", fontSize: "0.7rem" }}
							>
								Clique para confirmar ou pressione ↵
							</Typography>
						</Paper>
					</Popper>
				)}
			</Box>
		</Box>
	);
};

// Assign subcomponents
Chat.Message = Message;
Chat.Confirmation = ConfirmationMessage;
Chat.OAuthAction = OAuthActionMessage;

export { Chat };
export type { OAuthPendingItem, OAuthActionMessageProps } from "./OAuthActionMessage";
